import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../hooks/apiRequest";
import { ClientProps } from "../types/interfaces";
import { clientKeys } from "../utils/queryKeys";

export function useClients(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: clientKeys.list(),
    queryFn: async () => (await apiRequest<ClientProps[]>("clients")).data,
    enabled: options.enabled ?? true,
  });
}

export function useClient(id: string) {
  return useQuery({
    queryKey: clientKeys.detail(id),
    queryFn: async () => (await apiRequest<ClientProps>(`clients/${id}`)).data,
    enabled: !!id,
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; phone?: string; email?: string; billingAddress?: string }) =>
      (await apiRequest<ClientProps>("clients", { method: "POST", body: JSON.stringify(data) })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: clientKeys.all }),
  });
}

export function useUpdateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Pick<ClientProps, "name" | "phone" | "email" | "billingAddress" | "active">> }) =>
      (await apiRequest<ClientProps>(`clients/${id}`, { method: "PUT", body: JSON.stringify(data) })).data,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
      queryClient.invalidateQueries({ queryKey: clientKeys.detail(variables.id) });
    },
  });
}

export function useDeleteClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiRequest<void>(`clients/${id}`, { method: "DELETE" })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: clientKeys.all }),
  });
}
